/**
 *
 * UploadStl
 *
 */

import { useCallback, useEffect, useState } from "react";
import * as PropTypes from "prop-types";
import { useDropzone } from "react-dropzone";
import { IconButton, LinearProgress, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Delete as DeleteIcon } from "@material-ui/icons";
import uuid from "uuid/v4";
import { authorizedRequest, HTTP_METHODS } from "utils/request";
import axios from "axios";
import { FormattedMessage } from "react-intl";
import messages from "containers/ProductQuestionFlow/messages";
import moment from 'moment'

const CancelToken = axios.CancelToken;
// import styled from 'styled-components';

const useStyles = makeStyles(theme => ({
  dropzone: {
    height: "100px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    border: "1px dashed #454546",
    borderRadius: "15px",
    margin: theme.spacing(2),
  },
  contentWrapper: {
    margin: theme.spacing(2),
  },
  dropzoneText: {
    textAlign: "center",
  },
  content: {
    flex: 1,
    borderTop: ({ isAlternative }) =>
      isAlternative ? "none" : "1px solid #454546",
    marginTop: ({ isAlternative }) =>
      isAlternative ? theme.spacing(0) : theme.spacing(2),
    padding: ({ isAlternative }) =>
      isAlternative ? theme.spacing(0) : theme.spacing(2),
    overflow: "auto",
    marginBottom: ({ isAlternative }) =>
      isAlternative ? theme.spacing(0) : theme.spacing(2),
  },
  itemHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
}));

function UploadStl({
  fileUrl,
  documents = [],
  onChange,
  isAlternative = false,
  onChangeUploading,
}) {
  const classes = useStyles({
    isAlternative,
  });

  

    const [files, setFiles] = useState(documents);
    const [errorFiles, setErrorFiles] = useState([]);
    window.files = files
    window.setFiles = setFiles

  useEffect(
    () => {
      if (onChangeUploading) {
        onChangeUploading(files.findIndex(f => f.id === "") > -1);
      }
    },
    [files, onChangeUploading],
  );

  const onCancel = useCallback(fileId => {
    setFiles(fs => {
      const fileIndex = fs.findIndex(f => f.fileId === fileId);
      fs[fileIndex].cancelToken.cancel();
      return [...fs.slice(0, fileIndex), ...fs.slice(fileIndex + 1)];
    });
  }, []);

  const onDelete = useCallback(
    id => {
      const fileIndex = files.findIndex(doc => doc.id === id);

      authorizedRequest(files[fileIndex].fileUrl, {
        method: HTTP_METHODS.DELETE,
      }).then(() => {
        const newFiles = [
          ...files.slice(0, fileIndex),
          ...files.slice(fileIndex + 1),
        ];

        if (onChange) {
          onChange(newFiles);
        }

        setFiles(newFiles);
      });
    },
    [files, onChange],
  );

  const processFile = file => {
    const data = new FormData();
    data.append("file", file, file.name);
    const cancelToken = CancelToken.source();
    const fileId = uuid();

    setFiles(fs => [
      ...fs,
      {
        fileId,
        progress: 1,
        error: "",
        id: "",
        finish: false,
        cancelToken,
        name: file.name,
      },
    ]);

    const config = {
      config: {
        onUploadProgress: ({ loaded, total }) => {
          // Bypassing stale closure
          setFiles(fs => {
            const fileIndex = fs.findIndex(f => f.fileId === fileId);

            if (fileIndex === -1) {
              console.log("ERROR: Unable to find file with FileID", fileId);
              return fs;
            }

            return [
              ...fs.slice(0, fileIndex),
              {
                ...fs[fileIndex],
                progress: (loaded / total) * 100,
              },
              ...fs.slice(fileIndex + 1),
            ];
          });
        },
        cancelToken: cancelToken.token,
      },
      body: data,
      method: HTTP_METHODS.POST,
    };

    doRequest(config, fileId);
  };

  const doRequest = async (config, fileId) => {
    try {
      const response = await authorizedRequest(fileUrl, config);

      setFiles(fs => {
        const fileIndex = fs.findIndex(f => f.fileId === fileId);
        let newFiles = fs;

        if (fileIndex > -1) {
          newFiles = [
            ...fs.slice(0, fileIndex),
            {
              ...fs[fileIndex],
              ...response,
            },
            ...fs.slice(fileIndex + 1),
          ];
        }

        if (onChange) {
          onChange(newFiles);
        }

        return newFiles;
      });
    } catch (err) {
      setFiles(fs => {
        const fileIndex = fs.findIndex(f => f.fileId === fileId);
        let newFiles = fs;

        if (fileIndex > -1) {
          newFiles = [...fs.slice(0, fileIndex), ...fs.slice(fileIndex + 1)];

          setErrorFiles(errFiles => [
            ...errFiles,
            {
              ...fs[fileIndex],
              error: true,
            },
          ]);
        }

        if (onChange) {
          onChange(newFiles);
        }

        return newFiles;
      });
    }
  };

  const onDrop = (acceptedFiles = []) => {
    for (let i = 0; i < acceptedFiles.length; i++) {
      const file = acceptedFiles[i];

      processFile(file);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  return [
    <div
      key="dropzone"
      className={classes.dropzone}
      {...getRootProps()}
      data-cy="dropZone"
    >
      <input {...getInputProps()} />
      <span className={classes.dropzoneText}>
        {isDragActive ? (
          <p>
            <FormattedMessage {...messages.dropZoneTextActive} />
          </p>
        ) : (
          <p>
            <FormattedMessage {...messages.dropZoneText} />
          </p>
        )}
      </span>
    </div>,
    (files.length > 0 || errorFiles.length > 0) && (
      <div key="content" className={classes.content}>
        {errorFiles.map(({ name, id, fileId }) => (
          <div key={id || fileId} className={classes.item}>
            <div className={classes.itemHeader} data-cy="uploadedFile">
              <Typography noWrap>{name}</Typography>
              <IconButton
                onClick={() => {
                  setErrorFiles(errFiles =>
                    errFiles.filter(f => f.fileId !== fileId),
                  );
                }}
              >
                <DeleteIcon />
              </IconButton>
            </div>
            <LinearProgress
              color="secondary"
              variant="determinate"
              value={100}
            />
          </div>
        ))}
        {files.map(({ name, id, fileId, progress, cancelToken, createdAtAtString, createdAt }) => (
          <div key={id || fileId} className={classes.item}>
            <div className={classes.itemHeader} data-cy="uploadedFile">
              <Typography noWrap>{name}</Typography>
              <IconButton
                onClick={
                  id ? () => onDelete(id) : () => onCancel(fileId, cancelToken)
                }
              >
                <DeleteIcon />
              </IconButton>
            </div>
            <div>{moment(createdAt * 1000).subtract(1,'hours').format("DD/MM/YYYY HH:mm:ss")}</div>
            <LinearProgress
              color="primary"
              variant="determinate"
              value={progress || 100}
            />
            {progress < 100 && <span>{progress.toFixed(0)}%</span>}
          </div>
        ))}
      </div>
    ),
  ];
}

UploadStl.propTypes = {
  fileUrl: PropTypes.string.isRequired,
  documents: PropTypes.array,
  onChange: PropTypes.func,
  onChangeUploading: PropTypes.func,
};

export default UploadStl;
